import { useEffect, useMemo, useRef, useState } from "react";

export default function PlatformStatusPicker({
    statuses,
    value,
    currentStatus,
    onChange,
    testId,
}) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const rootRef = useRef(null);
    const selected = statuses.find((status) => status.code === value);

    const groups = useMemo(() => {
        const needle = query.trim().toLocaleLowerCase("ru-RU");
        const filtered = needle
            ? statuses.filter((status) => `${status.name} ${status.stage || ""}`.toLocaleLowerCase("ru-RU").includes(needle))
            : statuses;
        const result = new Map();
        filtered.forEach((status) => {
            const stage = status.stage || "Другие статусы";
            if (!result.has(stage)) result.set(stage, []);
            result.get(stage).push(status);
        });
        return Array.from(result.entries());
    }, [query, statuses]);

    useEffect(() => {
        if (!open) return undefined;
        const closeOutside = (event) => {
            if (!rootRef.current?.contains(event.target)) setOpen(false);
        };
        const closeOnEscape = (event) => {
            if (event.key === "Escape") setOpen(false);
        };
        document.addEventListener("mousedown", closeOutside);
        document.addEventListener("keydown", closeOnEscape);
        return () => {
            document.removeEventListener("mousedown", closeOutside);
            document.removeEventListener("keydown", closeOnEscape);
        };
    }, [open]);

    const choose = (status) => {
        onChange(status.code);
        setOpen(false);
        setQuery("");
    };

    return (
        <div ref={rootRef} className="relative mt-2 normal-case" data-testid={testId}>
            <button
                type="button"
                onClick={() => setOpen((current) => !current)}
                aria-haspopup="listbox"
                aria-expanded={open}
                className="flex w-full items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-left text-sm text-slate-900 transition hover:border-slate-300 hover:bg-white focus:border-[#cda834] focus:outline-none focus:ring-2 focus:ring-[#cda834]/30"
            >
                <span className="min-w-0">
                    <span className="block truncate font-semibold">{selected?.name || value || "Выберите статус"}</span>
                    <span className="mt-0.5 block text-xs text-slate-500">
                        {selected?.stage || "Этап не указан"}
                        {value === currentStatus ? " · текущий на платформе" : ""}
                    </span>
                </span>
                <svg className={`h-4 w-4 shrink-0 text-slate-400 transition ${open ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            {open ? (
                <div className="absolute left-0 right-0 z-30 mt-2 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
                    <div className="border-b border-slate-100 p-3">
                        <label className="sr-only" htmlFor={`${testId}-search`}>Найти статус</label>
                        <div className="relative">
                            <input
                                id={`${testId}-search`}
                                autoFocus
                                value={query}
                                onChange={(event) => setQuery(event.target.value)}
                                placeholder="Найти статус или этап…"
                                className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-sm outline-none focus:border-[#cda834] focus:bg-white"
                            />
                            <svg className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>
                    </div>
                    <div role="listbox" aria-label="Статусы HR-платформы" className="max-h-80 overflow-y-auto p-2">
                        {groups.length ? groups.map(([stage, stageStatuses]) => (
                            <section key={stage} className="mb-2 last:mb-0">
                                <p className="sticky top-0 bg-white/95 px-2 py-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-400 backdrop-blur">{stage}</p>
                                <div className="space-y-1">
                                    {stageStatuses.map((status) => {
                                        const isSelected = status.code === value;
                                        const isCurrent = status.code === currentStatus;
                                        return (
                                            <button
                                                key={status.code}
                                                type="button"
                                                role="option"
                                                aria-selected={isSelected}
                                                onClick={() => choose(status)}
                                                className={`flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-left text-sm transition ${isSelected ? "bg-amber-50 text-amber-950" : "text-slate-700 hover:bg-slate-50"}`}
                                            >
                                                <span className="font-medium">{status.name}</span>
                                                <span className="flex shrink-0 items-center gap-2">
                                                    {isCurrent ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-700">Текущий</span> : null}
                                                    {isSelected ? <span aria-hidden="true" className="text-[#9b7c13]">✓</span> : null}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </section>
                        )) : <p className="px-3 py-8 text-center text-sm text-slate-500">Статусы не найдены</p>}
                    </div>
                </div>
            ) : null}
        </div>
    );
}
